import classNames from 'classnames';
import React, { lazy, memo, Suspense, useCallback, useMemo, useRef } from 'react';

import { MaterialErrorMessage } from 'src/components/UI/common/MaterialErrorMessage';
import { useCleanHoverErrors } from 'src/components/UI/inputs/useHoverErrors';
import { FROALA_KEY } from 'src/config';
import { useInputCleanBaseHook } from 'src/utils/hooks/inputs';
import { SafeAnyType } from 'src/utils/safeAny';
import { StateFormInputOptionsType, StateFormReturnType } from 'src/utils/stateForm';

const FroalaLazy = lazy(() => import('src/components/UI/FroalaLazy'));

type Props = {
  formProps: StateFormReturnType;
  name: string;

  className?: string;
  disabled?: boolean;
  placeholder?: string;
  required?: boolean;
  heightMin?: number;
  heightMax?: number;
  validateName?: string;
  modal?: boolean; // to fix the insert link element
  config?: Record<string, SafeAnyType>;
  showHoverError?: boolean;
  errorLabel?: string;
};

export const UiCleanWYSIWYG: React.FC<Props> = memo(
  ({
    formProps: { onChange: formOnChange, register, unregister, getSubscribeProps, clearErrors },
    name,
    disabled = false,
    required,
    placeholder,
    heightMin = 100,
    heightMax = 200,
    validateName,
    modal,
    config: configProp,
    showHoverError = false,
    className,
    errorLabel,
  }: Props) => {
    const inputOptions = useMemo(
      (): StateFormInputOptionsType => ({
        required: required || false,
        errorLabel,
      }),
      [required, errorLabel]
    );

    const [value, errors] = useInputCleanBaseHook<string | null | undefined>({
      getSubscribeProps,
      name,
      inputOptions,
      unregister,
      register,
      type: 'wysiwyg',
      validateName,
    });
    const hasErrors = !!errors?.length;

    const { errorString, onChangeInputHover } = useCleanHoverErrors({
      name,
      validateName,
      clearErrors,
      errors,
      showAnyError: showHoverError,
    });

    const isFocusedRef = useRef(false);

    const config = useMemo(
      () => ({
        key: FROALA_KEY,
        heightMin,
        heightMax,
        useClasses: true,
        attribution: false,
        editorClass: 'fr-common',
        quickInsertEnabled: false,
        spellcheck: false,
        charCounterCount: false,
        imageUploadURL: '/froala/UploadImage', // doesn't work on dev
        videoUpload: false,
        placeholderText: placeholder,
        colorsBackground: [
          '#15E67F',
          '#E3DE8C',
          '#D8A076',
          '#D83762',
          '#76B6D8',
          'REMOVE',
          '#1C7A90',
          '#249CB8',
          '#4ABED9',
          '#FBD75B',
          '#FBE571',
          '#FFFFFF',
        ],
        colorsStep: 6,
        colorsText: [
          '#15E67F',
          '#E3DE8C',
          '#D8A076',
          '#D83762',
          '#76B6D8',
          'REMOVE',
          '#1C7A90',
          '#249CB8',
          '#4ABED9',
          '#FBD75B',
          '#FBE571',
          '#FFFFFF',
        ],
        toolbarButtons: (() => {
          let arr = [
            'bold',
            'italic',
            'underline',
            'fontSize',
            'strikeThrough',
            'subscript',
            'superscript',
            'textColor',
            'backgroundColor',
            'align',
            'formatOL',
            'formatUL',
            'outdent',
            'indent',
            'insertHR',
            'insertLink',
            'insertTable',
            'undo',
            'redo',
            'clearFormatting',
            'html',
            'insertImage',
            'insertVideo',
          ];

          if (modal) {
            // move these properties to the center of the modal (21615)
            const propertes = ['insertLink', 'insertImage', 'insertVideo'];

            arr = arr.filter((name) => !propertes.includes(name));

            arr.splice(0, 0, ...propertes);
          }

          return arr;
        })(),
        events: {
          initialized() {
            if (disabled) {
              // https://github.com/froala/react-froala-wysiwyg/issues/228
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              // eslint-disable-next-line react/no-this-in-sfc
              this.edit.off();
            }
          },
          focus() {
            isFocusedRef.current = true;
          },
          blur() {
            isFocusedRef.current = false;
          },
        },
        ...configProp,
      }),
      [configProp, disabled, heightMax, heightMin, modal, placeholder]
    );

    const onChange = useCallback(
      (text: string) => {
        if (isFocusedRef.current) {
          formOnChange(name, text.length > 0 ? text : null);
          onChangeInputHover();
        }
      },
      [formOnChange, name, onChangeInputHover]
    );

    return (
      <>
        <div
          className={classNames(className, {
            'wysiwyg-error': hasErrors,
            'fr-modal-box': modal,
          })}
        >
          <Suspense fallback={<div>Froala is loading...</div>}>
            <FroalaLazy config={config} model={value} onModelChange={onChange} />
          </Suspense>
        </div>
        <MaterialErrorMessage error={errorString} />
      </>
    );
  }
);
